import React from 'react';
import { Box, Heading, Loader, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { SparklesSmall } from '@wix/wix-ui-icons-common';
import { dataHooks } from '../../datahooks';
import { ButtonGrid } from '../../../../components/ButtonGrid';
import { useStoreNameData } from '../../context/storeNameAtoms';
import { useStoreSuggestions } from '../../hooks/useStoreSuggestions';
import { useStoreNameInput } from '../../hooks/useStoreNameInput';
import s from './StoreNameMain.scss';
import { StoreNameInput } from '../../components/StoreNameInput';
import { isEmpty } from 'lodash';

export function StoreNameMain() {
  const { t } = useTranslation();
  const storeNameData = useStoreNameData();
  const { getStoreNameSuggestions } = useStoreSuggestions();
  const { handleStoreNameChange, handleSuggestionSelect } = useStoreNameInput();

  const { selectedSuggestion, suggestions, isLoadingSuggestions, currentStoreName } = storeNameData;

  const suggestionItems = suggestions.map((suggestion) => ({
    key: suggestion,
    value: suggestion,
    label: suggestion,
    onClick: (value: string) => handleSuggestionSelect({ value, label: value }),
    dataHook: `${dataHooks.StoreNameSuggestion}-${suggestion}`,
  }));

  if (isLoadingSuggestions && isEmpty(suggestions)) {
    return (
      <Box align="center" verticalAlign="middle" height="100%" width="100%">
        <Loader size="large" />
      </Box>
    );
  }

  return (
    <Box direction="vertical" height="100%" width="100%" gap={4} dataHook={dataHooks.StoreNameMainContent}>
      <Box direction="vertical" gap={1}>
        <Heading className={s.textTitle} size="medium">
          {t('WHAT_YOUR_STORE_NAME')}
        </Heading>

        <Text className={s.textSubtitle}>{t('PICK_MEMORABLE_NAME_TO_REPRESENT_YOUR_BRAND')}</Text>

        <Text className={`${s.textDisclaimer} ${s.textSubtitle}`}>
          {t('WELL_CREATE_A_CONSISTENT_BRAND_BY_USING_YOUR_STORE_NAME_ACROSS_YOUR_SITE')}
        </Text>
      </Box>

      <StoreNameInput value={currentStoreName} onChange={handleStoreNameChange} />

      <Box direction="vertical" gap={2}>
        <Box align="space-between" verticalAlign="middle">
          <Text weight="bold">AI suggestions for you:</Text>

          <button
            onClick={getStoreNameSuggestions}
            className={s.generateButton}
            data-hook={`${dataHooks.StoreNameSuggestion}-generate`}
          >
            <Box align="center" gap={1} verticalAlign="middle">
              <SparklesSmall />

              <Text>{t('GENERATE_NEW_SUGGESTIONS')}</Text>
            </Box>
          </button>
        </Box>

        <ButtonGrid
          items={suggestionItems}
          selectedValue={selectedSuggestion}
          isDisabled={isLoadingSuggestions}
        />
      </Box>
    </Box>
  );
}
